import React, { useState, useEffect } from "react";
import axios from "axios";
import * as Yup from "yup";
import merge from "lodash/merge";
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFCheckbox,
  RHFSwitch,
  RHFUploadAvatar,
} from "../../../components/hook-form";
import { UploadService } from "../../../api/upload.service";
import { useCallback } from "react";
import {
  Box,
  Stack,
  Button,
  Tooltip,
  DialogActions,
  Typography,
  Divider,
  Avatar,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers";
import { yupResolver } from "@hookform/resolvers/yup";
import { IPatientSchema } from "../../../@types/patient";
import { enqueueSnackbar } from "notistack";
import {
  patientGeocodingLocationUrl,
  postalCode,
} from "../../../config-global";
import { PatientService } from "../../../api/patient.service";
import { useAuthContext } from "../../../auth/useAuthContext";
import { usePatientPermissions } from "../../../hooks/usePatientPermissions";
import NHSEyeTestEligibility, {
  NHSEligibilityData,
} from "../../../components/patient/NHSEyeTestEligibility";
import { useAppContext } from "../../../providers/AppContext";

import { getR2File } from "../../../utils/file";

type Props = {
  onCancel: VoidFunction;
  isEdit: Boolean;
  currentPatient?: IPatientSchema;
  Fields: Boolean;
  onSubmitForm: (patient?: any) => void;
  readOnly?: boolean;
};

type FormValuesProps = IPatientSchema & { gpAddress?: string };
const today = new Date();

const getAge = (date: Date) => {
  return (
    today.getFullYear() -
    date.getFullYear() -
    (today.getMonth() < date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() < date.getDate())
      ? 1
      : 0)
  );
};

const getInitialValues = (
  currentPatient: IPatientSchema | null | undefined,
) => {
  const initialEvent: FormValuesProps = {
    id: "",
    title: "Mr",
    firstName: "",
    surName: "",
    lastName: "",
    gp: "",
    gpAddress: "",
    date_of_birth: null,
    postCode: "",
    gender: "Male",
    phone: "",
    mobile: "",
    landline: "",
    email: "",
    town: "",
    county: "",
    houseNumber: "",
    street: "",
    address: "",
    age: null,
    notes: "",
    popupNotes: false,
    nhsEyeTestEligibility: {
      under16: false,
      over60: false,
      student: false,
      benefits: false,
      glaucomaDiabetes: false,
      familyHistory: false,
      other: false,
      otherDetails: "",
      evidenceSeen: false,
      auditLog: [],
    },
    communicationStyle: {
      method: undefined,
      customText: "",
    },
    consentSMS: false,
    consentEmail: false,
    consentPhone: false,
    consentPost: false,
    isActive: true,
  };

  if (currentPatient) {
    if (currentPatient.date_of_birth) {
      currentPatient.age = getAge(new Date(currentPatient.date_of_birth));
    }

    const gpDetailsName = (currentPatient as any)?.gpDetails?.name;
    const gpDetailsAddress = (currentPatient as any)?.gpDetails?.address;

    const gpMapped = {
      gp: gpDetailsName ?? currentPatient.gp ?? "",
      gpAddress: gpDetailsAddress ?? (currentPatient as any).gpAddress ?? "",
    } as Partial<FormValuesProps>;

    const pref = (currentPatient as any)?.patientPreference;
    const mappedConsents = {
      consentSMS:
        pref?.smsConsent ??
        (currentPatient as any).gdprSmsConsent ??
        currentPatient.consentSMS ??
        false,
      consentEmail:
        pref?.emailConsent ??
        (currentPatient as any).gdprEmailConsent ??
        currentPatient.consentEmail ??
        false,
      consentPhone:
        pref?.phoneConsent ??
        (currentPatient as any).gdprPhoneConsent ??
        currentPatient.consentPhone ??
        false,
      consentPost:
        pref?.postConsent ??
        (currentPatient as any).gdprPostConsent ??
        currentPatient.consentPost ??
        false,
    } as Partial<FormValuesProps>;

    const nhs = (currentPatient as any)?.patientNhs?.nhsEligibility;
    const nhsEyeTestEligibility = nhs
      ? {
          under16: !!nhs.isUnder16,
          over60: !!nhs.isOver60,
          student: !!nhs.isStudent,
          benefits: !!nhs.isBenefits,
          glaucomaDiabetes: !!nhs.isGlaucomaDiabetes,
          familyHistory: !!nhs.isFamilyHistory,
          other: !!nhs.isOther,
          otherDetails: (nhs.isOther as string) || "",
          evidenceSeen: !!nhs.isEvidenceSeen,
          auditLog: [],
        }
      : initialEvent.nhsEyeTestEligibility;

    const mappedCommunicationStyle =
      typeof (currentPatient as any).communicationStyle === "string"
        ? {
            method: (currentPatient as any).communicationStyle,
            customText: "",
          }
        : (currentPatient as any).communicationStyle ||
          initialEvent.communicationStyle;

    const mappedContact = {
      mobile:
        (currentPatient as any)?.mobile || (currentPatient as any)?.phone || "",
      phone:
        (currentPatient as any)?.phone ?? (currentPatient as any)?.mobile ?? "",
      landline: (currentPatient as any)?.landline ?? "",
      postCode:
        (currentPatient as any)?.postCode ??
        (currentPatient as any)?.postalCode ??
        "",
      houseNumber:
        (currentPatient as any)?.houseNumber ??
        (currentPatient as any)?.address?.houseNumber ??
        "",
      street:
        (currentPatient as any)?.street ??
        (currentPatient as any)?.address?.street ??
        "",
      town:
        (currentPatient as any)?.town ??
        (currentPatient as any)?.address?.city ??
        "",
      county:
        (currentPatient as any)?.county ??
        (currentPatient as any)?.address?.province ??
        "",
      address:
        ((currentPatient as any)?.address ??
          [
            (currentPatient as any)?.houseNumber,
            (currentPatient as any)?.street,
            (currentPatient as any)?.town,
            (currentPatient as any)?.county,
            (currentPatient as any)?.postCode ??
              (currentPatient as any)?.postalCode,
          ]
            .filter(Boolean)
            .join(", ")) ||
        "",
    } as Partial<FormValuesProps>;

    return merge(
      {},
      initialEvent,
      currentPatient,
      gpMapped,
      mappedConsents,
      { communicationStyle: mappedCommunicationStyle },
      { nhsEyeTestEligibility },
      mappedContact,
    );
  }

  return initialEvent;
};
const titles = [
  { id: "Mr", name: "Mr" },
  { id: "Mrs", name: "Mrs" },
  { id: "Miss", name: "Miss" },
  { id: "Ms", name: "Ms" },
  { id: "Mx", name: "Mx" },
  { id: "Dr", name: "Dr" },
  { id: "Prof", name: "Prof" },
];
const AddPatient = ({
  onCancel,
  isEdit,
  currentPatient,
  Fields,
  onSubmitForm,
  readOnly = false,
}: Props) => {
  const { user } = useAuthContext();
  const perms = usePatientPermissions(user);
  const [showFields, setShowFields] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(Fields);
  const [checkPostCodeString, setCheckPostCodeString] = useState("");
  const [town, setTown] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [county, setCounty] = useState("");
  const [street, setStreet] = useState("");

  const PatientSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    surName: Yup.string().required("Surname is required"),
    lastName: Yup.string().required("Salutation is required"),
    email: Yup.string()
      .required("Email is required")
      .email("Email must be a valid email address"),
    gender: Yup.string().required("Gender is required"),
    date_of_birth: Yup.date().required("Date of birth is required"),
    mobile: Yup.string().required("Mobile is required"),
    postCode: Yup.string().required("Post Code is required"),
    address: Yup.string().required("Full Address is required"),
    notes: Yup.string().required("Notes are required"),
    gp: Yup.string().required("GP Name is required"),
    gpAddress: Yup.string().required("GP Address is required"),
  });

  const methods = useForm<any>({
    resolver: yupResolver(PatientSchema),
    defaultValues: getInitialValues(currentPatient),
  });
  const [postalCodeResponse, setPostalCodeResponse] = useState<any[]>([]);

  useEffect(() => {
    const parts = [houseNumber, street, town, county, checkPostCodeString]
      .map((s) => String(s || "").trim())
      .filter(Boolean);
    const hasAll = [
      houseNumber,
      street,
      town,
      county,
      checkPostCodeString,
    ].every((s) => String(s || "").trim().length > 0);
    if (hasAll && parts.length) {
      try {
        methods.setValue("address", parts.join(", "));
      } catch {}
    }
  }, [houseNumber, street, town, county, checkPostCodeString, methods]);
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
    getValues,
  } = methods;

  const patientService = new PatientService();
  const uploadService = new UploadService();

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        setValue(
          "imageUrl",
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }) as any,
          { shouldValidate: true },
        );
      }
    },
    [setValue],
  );

  const lastDuplicateKeyRef = React.useRef<string | null>(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const normalizeName = (s: any) =>
    String(s ?? "")
      .trim()
      .toLowerCase();

  const normalizeDateParts = (input: any) => {
    if (!input) return null;
    try {
      let d: Date;
      if (input instanceof Date) {
        d = input;
      } else if (typeof input === "string") {
        const s = input.trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
          d = new Date(s);
        } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
          const [day, month, year] = s.split("/").map(Number);
          d = new Date(year, month - 1, day);
        } else {
          d = new Date(s);
        }
      } else if (input?.$d) {
        d = new Date(input.$d);
      } else if (typeof input === "object" && input?.toDate) {
        d = new Date(input.toDate());
      } else {
        d = new Date(input);
      }
      if (isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return { y, m, day };
    } catch (e) {
      return null;
    }
  };

  // Main duplicate check used by onBlur handlers
  const checkDuplicateByNameDob = (
    overrides?: Partial<{
      firstName: string;
      surName: string;
      date_of_birth: any;
    }>,
  ) => {
    (async () => {
      try {
        const current = getValues();
        const firstName = overrides?.firstName ?? current.firstName;
        const surName = overrides?.surName ?? current.surName;
        const date_of_birth = overrides?.date_of_birth ?? current.date_of_birth;

        if (!firstName || !surName || !date_of_birth) return;

        const nameKey = `${normalizeName(firstName)}|${normalizeName(surName)}`;
        const dobParts = normalizeDateParts(date_of_birth);
        if (!dobParts) return;
        const key = `${nameKey}|${dobParts.y}-${dobParts.m}-${dobParts.day}`;
        if (lastDuplicateKeyRef.current === key || isCheckingDuplicate) return;

        setIsCheckingDuplicate(true);
        lastDuplicateKeyRef.current = key;

        const dateOfBirthISO = `${dobParts.y}-${dobParts.m}-${dobParts.day}`;
        const response = await patientService.checkDuplicateByNameDob(
          firstName,
          surName,
          dateOfBirthISO,
        );
        const { duplicates } = response.data || {};
        let showWarning = false;
        if (Array.isArray(duplicates) && duplicates.length > 0) {
          const dobISO = `${dobParts.y}-${dobParts.m}-${dobParts.day}`;
          showWarning = duplicates.some((p: any) => {
            const f = normalizeName(p?.firstName);
            const s = normalizeName(p?.surName);
            const d = normalizeDateParts(p?.date_of_birth);
            const iso = d ? `${d.y}-${d.m}-${d.day}` : null;
            return (
              f === normalizeName(firstName) &&
              s === normalizeName(surName) &&
              iso === dobISO
            );
          });
        } else if (response.data?.isDuplicate === true) {
          // Only show generic duplicate if all three fields are present in request
          showWarning = Boolean(firstName && surName && dobParts);
        }

        if (showWarning) {
          enqueueSnackbar(
            `Possible duplicate found: ${firstName} ${surName} with the same DOB already exists.`,
            { variant: "warning", autoHideDuration: 4000 },
          );
        }
      } catch (error) {
        // Do not block user input
      } finally {
        setIsCheckingDuplicate(false);
      }
    })();
  };

  const handleDOBBlur = () => {
    checkDuplicateByNameDob();
  };

  // State for tracking consent changes
  const [consentChanges, setConsentChanges] = useState<any[]>([]);

  // State for NHS Eye Test Eligibility
  const [nhsEligibility, setNhsEligibility] = useState<NHSEligibilityData>(
    currentPatient?.nhsEyeTestEligibility || {
      under16: false,
      over60: false,
      student: false,
      benefits: false,
      glaucomaDiabetes: false,
      familyHistory: false,
      other: false,
      otherDetails: "",
      evidenceSeen: false,
      auditLog: [],
    },
  );

  const watchedConsentSMS = watch("consentSMS");
  const watchedConsentEmail = watch("consentEmail");
  const watchedConsentPhone = watch("consentPhone");
  const watchedConsentPost = watch("consentPost");

  const logConsentChange = (
    consentType: string,
    newValue: boolean,
    oldValue: boolean,
  ) => {
    if (newValue !== oldValue) {
      const changeLog = {
        consentType,
        newValue,
        oldValue,
        timestamp: new Date().toISOString(),
        staffUserID: user?.id || user?.userId || "SYSTEM",
        changeReason: newValue ? "Consent granted" : "Consent withdrawn",
      };

      setConsentChanges((prev) => [...prev, changeLog]);
    }
  };

  useEffect(() => {
    const initialConsents = getInitialValues(currentPatient);
    if (watchedConsentSMS !== undefined) {
      logConsentChange(
        "SMS",
        watchedConsentSMS,
        initialConsents.consentSMS || false,
      );
    }
  }, [watchedConsentSMS]);

  useEffect(() => {
    const initialConsents = getInitialValues(currentPatient);
    if (watchedConsentEmail !== undefined) {
      logConsentChange(
        "Email",
        watchedConsentEmail,
        initialConsents.consentEmail || false,
      );
    }
  }, [watchedConsentEmail]);

  useEffect(() => {
    const initialConsents = getInitialValues(currentPatient);
    if (watchedConsentPhone !== undefined) {
      logConsentChange(
        "Phone",
        watchedConsentPhone,
        initialConsents.consentPhone || false,
      );
    }
  }, [watchedConsentPhone]);

  useEffect(() => {
    const initialConsents = getInitialValues(currentPatient);
    if (watchedConsentPost !== undefined) {
      logConsentChange(
        "Post",
        watchedConsentPost,
        initialConsents.consentPost || false,
      );
    }
  }, [watchedConsentPost]);

  const watchedTitle = watch("title");
  const watchedSurname = watch("surName");

  useEffect(() => {
    if (watchedTitle && watchedSurname) {
      const generatedSalutation = `${watchedTitle} ${watchedSurname}`;
      setValue("lastName", generatedSalutation);
    }
  }, [watchedTitle, watchedSurname, setValue]);

  useEffect(() => {
    const currentTitle = watch("title");
    const currentSurname = watch("surName");
    const currentLastName = watch("lastName");

    if (currentTitle && currentSurname && !currentLastName) {
      const salutation = `${currentTitle} ${currentSurname}`;
      setValue("lastName", salutation);
    }
  }, []);

  useEffect(() => {
    const currentDOB = watch("date_of_birth");
    if (isEdit && currentDOB) {
      handleChangeDate(currentDOB);
    }
  }, [isEdit]);

  useEffect(() => {
    const currentTitle = watch("title");
    const currentSurname = watch("surName");

    if (currentTitle && currentSurname && !watch("lastName")) {
      const generatedSalutation = `${currentTitle} ${currentSurname}`;
      setValue("lastName", generatedSalutation);
    }
  }, []);

  const checkPostCode = async () => {
    setShowFields(true);
    const config = {
      maxBodyLength: Infinity,
      headers: {
        ApiKey: postalCode,
      },
    };
    axios
      .get(
        `${patientGeocodingLocationUrl}?postalCode=${checkPostCodeString}&countryFilter=GB`,
        config,
      )
      .then((res) => {
        setPostalCodeResponse(res.data.locations);
      });
  };

  const getAddress = (index: number) => {
    setShowFields(false);
    setShowAddressFields(true);
    setTown(postalCodeResponse[index]?.address.city);
    setHouseNumber(postalCodeResponse[index]?.address.houseNumber);
    setCounty(postalCodeResponse[index]?.address.province);
    setStreet(postalCodeResponse[index]?.address.street);
  };

  const { getPatients } = useAppContext();
  const onSubmit = async (data: FormValuesProps) => {
    if (!(data.consentSMS || data.consentEmail || data.consentPhone)) {
      enqueueSnackbar(
        "Please select at least one contact method: SMS, Email, or Phone",
        { variant: "warning" },
      );
      return;
    }

    let finalImageUrl =
      typeof data.imageUrl === "string" ? data.imageUrl : undefined;

    if (data.imageUrl && (data.imageUrl as any) instanceof File) {
      try {
        const response = await uploadService.uploadFile(
          data.imageUrl as any,
          "patients",
        );
        if (response.data?.url) {
          finalImageUrl = response.data.url;
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Image upload failed", { variant: "error" });
        return;
      }
    }

    const selectedMethod =
      data.communicationStyle?.method ||
      (data.consentPhone
        ? "Phone"
        : data.consentSMS
          ? "SMS"
          : data.consentEmail
            ? "Email"
            : undefined);
    const dobParts = normalizeDateParts(data.date_of_birth);
    const dobISO = dobParts
      ? `${dobParts.y}-${dobParts.m}-${dobParts.day}`
      : null;
    const payload = {
      title: data.title,
      firstName: data.firstName,
      surName: data.surName,
      lastName: data.lastName,
      date_of_birth: dobISO,

      gp: data.gp,
      gpDetails: {
        name: data.gp,
        address: data.gpAddress,
      },
      phone: data.mobile || data.phone,
      landline: data.landline,
      email: data.email,
      gender: data.gender ? data.gender : "Male",
      postCode: checkPostCodeString,
      houseNumber: houseNumber,
      town: town,
      county: county,
      street: street,
      address: data.address,
      notes: data.notes,
      popupNotes: data.popupNotes,
      gdprSmsConsent: data.consentSMS,
      gdprEmailConsent: data.consentEmail,
      gdprPhoneConsent: data.consentPhone,
      gdprPostConsent: data.consentPost,
      nhsEligibility: {
        nhsEligibility: {
          isUnder16: nhsEligibility.under16,
          isOver60: nhsEligibility.over60,
          isStudent: nhsEligibility.student,
          isBenefits: nhsEligibility.benefits,
          isGlaucomaDiabetes: nhsEligibility.glaucomaDiabetes,
          isFamilyHistory: nhsEligibility.familyHistory,
          isOther: nhsEligibility.other
            ? nhsEligibility.otherDetails
            : undefined,
          isEvidenceSeen: nhsEligibility.evidenceSeen,
        },
      },
      communicationStyle: selectedMethod,
      isActive: data.isActive,
      imageUrl: finalImageUrl,
    };
    // Check duplicates on submission (non-blocking warning)
    // checkDuplicateByNameDob({ firstName: data.firstName, surName: data.surName, date_of_birth: data.date_of_birth });

    // Use top-level instance to avoid redundant instantiation
    patientService
      .createPatient(payload)
      .then(function (response) {
        onCancel();
        enqueueSnackbar("Patient Created Successfully!", {
          variant: "success",
        });
        getPatients();
        localStorage.removeItem("draftPrescreening");
        onSubmitForm(response.data);
      })
      .catch(function (error) {
        enqueueSnackbar(`${error?.message}`, {
          variant: "error",
        });
      });
  };

  const onUpdate = async (data: FormValuesProps) => {
    if (!(data.consentSMS || data.consentEmail || data.consentPhone)) {
      enqueueSnackbar(
        "Please select at least one contact method: SMS, Email, or Phone",
        { variant: "warning" },
      );
      return;
    }

    let finalImageUrl =
      typeof data.imageUrl === "string" ? data.imageUrl : undefined;

    if (data.imageUrl && (data.imageUrl as any) instanceof File) {
      try {
        const response = await uploadService.uploadFile(
          data.imageUrl as any,
          "patients",
        );
        if (response.data?.url) {
          finalImageUrl = response.data.url;
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Image upload failed", { variant: "error" });
        return;
      }
    }

    const selectedMethod =
      data.communicationStyle?.method ||
      (data.consentPhone
        ? "Phone"
        : data.consentSMS
          ? "SMS"
          : data.consentEmail
            ? "Email"
            : undefined);
    const dobParts = normalizeDateParts(data.date_of_birth);
    const dobISO = dobParts
      ? `${dobParts.y}-${dobParts.m}-${dobParts.day}`
      : null;
    const payload = {
      title: data.title,
      firstName: data.firstName,
      surName: data.surName,
      lastName: data.lastName,
      date_of_birth: dobISO,
      gp: data.gp,
      gpDetails: {
        name: data.gp,
        address: data.gpAddress,
      },
      phone: data.mobile || data.phone,
      landline: data.landline,
      email: data.email,
      gender: data.gender ? data.gender : "Male",
      postCode: checkPostCodeString,
      houseNumber: houseNumber,
      town: town,
      county: county,
      street: street,
      address: data.address,
      notes: data.notes,
      popupNotes: data.popupNotes,
      gdprSmsConsent: data.consentSMS,
      gdprEmailConsent: data.consentEmail,
      gdprPhoneConsent: data.consentPhone,
      gdprPostConsent: data.consentPost,
      nhsEligibility: {
        nhsEligibility: {
          isUnder16: nhsEligibility.under16,
          isOver60: nhsEligibility.over60,
          isStudent: nhsEligibility.student,
          isBenefits: nhsEligibility.benefits,
          isGlaucomaDiabetes: nhsEligibility.glaucomaDiabetes,
          isFamilyHistory: nhsEligibility.familyHistory,
          isOther: nhsEligibility.other
            ? nhsEligibility.otherDetails
            : undefined,
          isEvidenceSeen: nhsEligibility.evidenceSeen,
        },
      },
      communicationStyle: selectedMethod,
      isActive: data.isActive,
      imageUrl: finalImageUrl,
    };
    // Check duplicates on update submission (non-blocking warning)
    // checkDuplicateByNameDob({ firstName: data.firstName, surName: data.surName, date_of_birth: data.date_of_birth });

    patientService
      .updatePatient(currentPatient?.id, payload)
      .then(function (response) {
        onCancel();
        onSubmitForm();
        enqueueSnackbar("Patient Updated Successfully!", {
          variant: "success",
        });
      })
      .catch(function (error) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      });
  };

  const [isUnder16, setIsUnder16] = useState(false);

  const handleChangeDate = (dateOfBirth: Date) => {
    setValue("age", getAge(dateOfBirth));

    const today = new Date();
    const age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();
    const dayDiff = today.getDate() - dateOfBirth.getDate();

    let exactAge = age;
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      exactAge--;
    }

    if (exactAge < 16) {
      setIsUnder16(true);
      enqueueSnackbar("Patient is under 16 — adult supervision required.", {
        variant: "warning",
        autoHideDuration: 5000,
      });
    } else {
      setIsUnder16(false);
    }
  };

  const handleChange = (value: string) => {
    if (value === "" || /[A-Za-z\d\s]+$/.test(value)) {
      setCheckPostCodeString(value.toUpperCase());
    }
  };

  const handleTitleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const selectedTitle = event.target.value;
    setValue("title", selectedTitle);

    // Auto-set gender based on title selection
    switch (selectedTitle) {
      case "Mr":
        setValue("gender", "Male");
        break;
      case "Mrs":
      case "Miss":
      case "Ms":
        setValue("gender", "Female");
        break;
      case "Mx":
        setValue("gender", "Non-Binary");
        break;
      case "Dr":
      case "Prof":
        setValue("gender", "");
        break;
      default:
        setValue("gender", "");
        break;
    }
  };

  useEffect(() => {
    const nhs = (currentPatient as any)?.patientNhs?.nhsEligibility;
    if (nhs) {
      setNhsEligibility({
        under16: !!nhs.isUnder16,
        over60: !!nhs.isOver60,
        student: !!nhs.isStudent,
        benefits: !!nhs.isBenefits,
        glaucomaDiabetes: !!nhs.isGlaucomaDiabetes,
        familyHistory: !!nhs.isFamilyHistory,
        other: !!nhs.isOther,
        otherDetails: (nhs.isOther as string) || "",
        evidenceSeen: !!nhs.isEvidenceSeen,
        auditLog: [],
      });
    }
  }, [currentPatient]);

  useEffect(() => {
    const cp: any = currentPatient || {};
    if (cp.postCode || cp.postalCode) {
      setCheckPostCodeString(String(cp.postCode ?? cp.postalCode));
    }
    if (cp.houseNumber || cp.address?.houseNumber) {
      setHouseNumber(String(cp.houseNumber ?? cp.address?.houseNumber ?? ""));
    }
    if (cp.street || cp.address?.street) {
      setStreet(String(cp.street ?? cp.address?.street ?? ""));
    }
    if (cp.town || cp.address?.city) {
      setTown(String(cp.town ?? cp.address?.city ?? ""));
    }
    if (cp.county || cp.address?.province) {
      setCounty(String(cp.county ?? cp.address?.province ?? ""));
    }
  }, [currentPatient]);

  return (
    <FormProvider
      methods={methods}
      onSubmit={handleSubmit(isEdit ? onUpdate : onSubmit)}>
      <Stack
        spacing={3}
        sx={{ mt: 3, px: 3, pointerEvents: readOnly ? "none" : "auto" }}>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}>
          {readOnly ? (
            <>
              <Avatar
                src={
                  (getValues("imageUrl")
                    ? getR2File(getValues("imageUrl"))
                    : getR2File(currentPatient?.imageUrl)) as string
                }
                alt="Patient Photo"
                sx={{ width: 150, height: 150, mb: 1 }}
                imgProps={{ referrerPolicy: "no-referrer" }}
              />
            </>
          ) : (
            <RHFUploadAvatar
              name="imageUrl"
              maxSize={3145728}
              onDrop={handleDrop}
              helperText={
                <Typography
                  variant="caption"
                  sx={{
                    mt: 2,
                    mx: "auto",
                    display: "block",
                    textAlign: "center",
                    color: "text.secondary",
                  }}>
                  Allowed *.jpeg, *.jpg, *.png, *.gif
                  <br /> max size of {3145728 / 1024 / 1024}MB
                </Typography>
              }
            />
          )}
        </Box>
        <Box sx={{ display: "flex" }}>
          <RHFSelect
            sx={{ mr: 1, width: "30%" }}
            native
            name="title"
            label="Title"
            autoFocus
            tabIndex={1}
            onChange={handleTitleChange}
            InputLabelProps={{ shrink: true }}>
            {titles.map((title) => (
              <option key={title.id} value={title.id}>
                {title.name}
              </option>
            ))}
          </RHFSelect>
          <RHFTextField
            // error={false}
            name="firstName"
            label="First Name*"
            tabIndex={2}
            onBlur={() => {
              const dob = getValues("date_of_birth");
              if (dob) checkDuplicateByNameDob();
            }}
            onChange={(e) => {
              const capitalized = e.target.value
                .split(" ")
                .map(
                  (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
                )
                .join(" ");
              setValue("firstName", capitalized);
            }}
          />
          <RHFTextField
            sx={{ ml: 1 }}
            // error={false}
            name="surName"
            label="Surname*"
            tabIndex={3}
            onBlur={() => {
              const dob = getValues("date_of_birth");
              if (dob) checkDuplicateByNameDob();
            }}
            onChange={(e) => {
              const capitalized = e.target.value
                .split(" ")
                .map(
                  (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
                )
                .join(" ");
              setValue("surName", capitalized);
            }}
          />
          <RHFTextField
            sx={{ ml: 1 }}
            error={false}
            name="lastName"
            label="Salutation"
            tabIndex={4}
          />
        </Box>
        <Box sx={{ display: "flex" }}>
          <RHFSelect
            native
            name="gender"
            label="Gender"
            tabIndex={5}
            InputLabelProps={{ shrink: true }}>
            <option key="Male" value="Male">
              Male
            </option>
            <option key="Female" value="Female">
              Female
            </option>
            <option key="Non-Binary" value="Non-Binary">
              Non-Binary
            </option>
            <option key="Other" value="Other">
              Other
            </option>
            <option value=""> </option>
          </RHFSelect>
          <Controller
            name="date_of_birth"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                sx={{
                  ml: 2,
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: isUnder16 || error ? "#f44336" : undefined,
                      borderWidth: isUnder16 || error ? "2px" : undefined,
                    },
                  },
                }}
                onChange={(newValue: Date | any) => {
                  field.onChange(newValue);
                  if (newValue) {
                    handleChangeDate(newValue);
                  } else {
                    setIsUnder16(false);
                  }
                }}
                onClose={handleDOBBlur}
                label="Date Of Birth*"
                views={["year", "month", "day"]}
                format="dd/MM/yyyy"
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    tabIndex: 6,
                    error: !!error || isUnder16,
                    helperText:
                      error?.message ||
                      (isUnder16 ? "Must be 16 or older" : ""),
                  },
                }}
              />
            )}
          />
        </Box>
        <Box sx={{ display: "flex" }}>
          <RHFTextField
            // error={false}
            name="mobile"
            type="tel"
            label="Mobile*"
            tabIndex={7}
          />
          <RHFTextField
            sx={{ ml: 2 }}
            // error={false}
            name="landline"
            type="tel"
            label="Landline"
            tabIndex={8}
          />
          <RHFTextField
            sx={{ ml: 2 }}
            // error={false}
            name="email"
            type="email"
            label="Email*"
            tabIndex={9}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
          <Controller
            name="postCode"
            control={control}
            defaultValue=""
            rules={{
              validate: (v) =>
                v === "" ||
                /[A-Za-z\d\s]+$/.test(v) ||
                "Only alphanumeric characters are allowed",
            }}
            render={({ field, fieldState: { error } }) => (
              <RHFTextField
                {...field}
                label="Post Code*"
                error={!!error}
                value={checkPostCodeString}
                helperText={error ? error.message : ""}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  handleChange(value);
                  field.onChange(value);
                }}
                sx={{ minWidth: 200 }}
              />
            )}
          />
          <Button
            variant="contained"
            disabled={checkPostCodeString.length > 0 ? false : true}
            onClick={() => checkPostCode()}>
            Check Postcode
          </Button>
        </Box>

        {showFields && postalCodeResponse.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select an address:
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                border: 1,
                borderColor: "divider",
                p: 1,
              }}>
              {postalCodeResponse.map((location, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  onClick={() => getAddress(index)}
                  sx={{
                    display: "block",
                    textAlign: "left",
                    mb: 1,
                    textTransform: "none",
                  }}
                  fullWidth>
                  {location.address.houseNumber} {location.address.street},{" "}
                  {location.address.city}, {location.address.province}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {showAddressFields && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <RHFTextField
                name="houseNumber"
                label="House Number"
                value={houseNumber}
                onChange={(e) => setHouseNumber(e.target.value)}
              />
              <RHFTextField
                name="street"
                label="Street"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                sx={{ flexGrow: 1 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <RHFTextField
                name="town"
                label="Town/City"
                value={town}
                onChange={(e) => setTown(e.target.value)}
                sx={{ flexGrow: 1 }}
              />
              <RHFTextField
                name="county"
                label="County"
                value={county}
                onChange={(e) => setCounty(e.target.value)}
                sx={{ flexGrow: 1 }}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
          <RHFTextField
            name="address"
            label="Full Address*"
            multiline
            rows={3}
            tabIndex={12}
            placeholder="Enter full address"
            disabled={!perms.canEditPatient && !!isEdit}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
          <RHFTextField
            name="notes"
            label="Notes*"
            multiline
            rows={3}
            tabIndex={13}
            placeholder="Enter patient notes"
            disabled={!perms.canEditPatient && !!isEdit}
          />
          <RHFSwitch
            sx={{ mt: 2 }}
            name="popupNotes"
            label="Popup Notes"
            disabled={!perms.canEditPatient && !!isEdit}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Contact Methods*
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 3,
              flexWrap: "wrap",
            }}>
            <RHFCheckbox
              name="consentSMS"
              label="SMS"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
              disabled={!perms.canEditPatient && !!isEdit}
            />
            <RHFCheckbox
              name="consentEmail"
              label="Email"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
              disabled={!perms.canEditPatient && !!isEdit}
            />
            <RHFCheckbox
              name="consentPhone"
              label="Phone"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
              disabled={!perms.canEditPatient && !!isEdit}
            />
            <RHFCheckbox
              name="consentPost"
              label="Postal"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
              disabled={!perms.canEditPatient && !!isEdit}
            />
          </Box>
          {submitAttempted &&
            !(
              (watchedConsentSMS as boolean) ||
              (watchedConsentEmail as boolean) ||
              (watchedConsentPhone as boolean)
            ) && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}>
                Please select at least one contact method (SMS, Email, or Phone)
              </Typography>
            )}
        </Box>

        {/* NHS Eye Test Eligibility Section */}
        <NHSEyeTestEligibility
          eligibilityData={nhsEligibility}
          onUpdateEligibility={setNhsEligibility}
          disabled={!perms.canEditPatient && !!isEdit}
        />

        {/* GP Information Section */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <RHFTextField name="gp" label="GP Name*" tabIndex={10} />
            <RHFTextField
              name="gpAddress"
              label="GP Address*"
              tabIndex={11}
              multiline
              minRows={3}
            />
          </Box>
        </Box>
      </Stack>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        {!readOnly && (
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={(!!isEdit && !perms.canEditPatient) || isUploadingImage}
            onClick={() => setSubmitAttempted(true)}>
            {isEdit ? "Update" : "Add"}
          </LoadingButton>
        )}
      </DialogActions>
    </FormProvider>
  );
};

export default AddPatient;
